import React, { useCallback, useState } from "react";
import { checkNewPolygon } from "../label_processing/label_processing";
import {
  fetchUnmarkedMarkedList,
  fetchSequence,
} from "../networking/sequenceControllerNetwork";
import {
  Selection,
  ContextProps,
  SequenceDataProps,
  Mark,
} from "../@interfaces/interfaces";
import { adjustToScale } from "../label_processing/label_processing";

import { frameMinusOne, framePlusOne } from "../label_processing/helper";

export const Context = React.createContext<ContextProps | null>(null);

const ContextProvider = ({ children }: any) => {
  const [drawingMode, setDrawingMode] = useState<string>("polygon");
  const [sequenceDataPrev, setSequenceDataPrev] = useState<SequenceDataProps>({
    sequenceName: "",
    images: [],
  });
  const [selectionsPrev, setSelectionsPrev] = useState<Selection[]>([]);

  const [sequenceDataNext, setSequenceDataNext] = useState<SequenceDataProps>({
    sequenceName: "",
    images: [],
  });
  const [selectionsNext, setSelectionsNext] = useState<Selection[]>([]);

  const [sequenceData, setSequenceData] = useState<SequenceDataProps>({
    sequenceName: "",
    images: [],
  });
  const [selections, setSelections] = useState<Selection[]>([]);

  const [fullImageRatioToOg, setFullImageRatioToOg] = useState<number>(1);
  const [fullScreenWidth, setFullScreenWidth] = useState<number>(1);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [currentImageRect, setCurrentImageRect] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const [prev, setPrev] = useState<Selection[]>([]);
  const [currentTag, setCurrentTag] = useState<"day" | "night">("day");

  const clearSelectionHistory = useCallback(() => {
    return;
  }, []);

  const addNewSelection = (newSelection: Selection, action: string) => {
    if (
      JSON.stringify(newSelection.selection.edges[0]) ===
      JSON.stringify(newSelection.selection.edges[2])
    ) {
      return 0;
    }
    const currentImageSelections = selections.filter(
      (el: Selection) => el.imageId === currentImageIndex,
    );
    newSelection.selection.selectionId = currentImageSelections.length;
    newSelection.selection.tag = currentTag;

    // Only check intersections against selections with the same tag so that
    // day and night annotations never merge, union, or erase each other.
    const sameTagSelections = currentImageSelections.filter(
      (el: Selection) => el.selection.tag === newSelection.selection.tag,
    );

    const intersection = checkNewPolygon(
      newSelection,
      sameTagSelections,
      action,
    );

    if (intersection === 0) {
      if (action !== "delete") {
        setSelections((prevItems: Selection[]) => [...prevItems, newSelection]);
      }
    } else {
      setSelections((prevItems: Selection[]) => [
        ...prevItems.filter((item) => item.selection.edges.length > 0),
      ]);
    }
  };

  const copyPreviousToCurrent = (prev: Selection[]) => {
    const currentImageSelections = selections.filter(
      (el: Selection) => el.imageId === currentImageIndex,
    );

    // deep copy prev into setselections but change the imageId to currentImageIndex
    if (
      prev.length > 0 &&
      (currentImageSelections.length === 0 ||
        currentImageSelections.filter(
          (item) => item.selection.edges.length === 0,
        ))
    ) {
      const newPrev = JSON.parse(JSON.stringify(prev));
      newPrev.forEach((item: Selection) => (item.imageId = currentImageIndex));
      setSelections((prevItems: Selection[]) => [...prevItems, ...newPrev]);
    }
  };

  const getUnmarkedMarked = async (
    token: string,
    sequence_name: string,
  ): Promise<{ marked: Mark[]; folder_type: string }> => {
    let data = { marked: [], folder_type: "" };
    try {
      const response = await fetchUnmarkedMarkedList(token, sequence_name);
      data.marked = response.data.marked;
      data.folder_type = response.folder_type;
    } catch (error) {
      console.log(error);
    }
    return data;
  };

  // Refs that track which frame name is currently buffered in sequenceDataNext/Prev.
  // Used to validate the cache before using it during sequential navigation.
  const nextFrameRef = React.useRef<string>("");
  const prevFrameRef = React.useRef<string>("");

  const fetchAndSetImages = async (
    token: string,
    sequence_name: string,
    frame: string,
    setSequenceDataFunction: React.Dispatch<
      React.SetStateAction<SequenceDataProps>
    >,
    onLoaded?: (frame: string) => void,
  ): Promise<Selection[]> => {
    try {
      const response = await fetchSequence(token, sequence_name, frame);

      if (response.data.images) {
        const base64Mod = response.data.images.map(
          (item: { imageName: string; image: string }) => {
            return {
              imageName: item.imageName,
              image: "data:image/jpeg;base64," + item.image,
            };
          },
        );
        setSequenceDataFunction({
          sequenceName: response.data.sequenceName,
          images: base64Mod,
        });
        if (onLoaded) onLoaded(frame);
      }
      return response.data.selection[0].selections;
    } catch (error) {
      return [];
    }
  };

  const switchExistingStatesFetchNewImagesDependingOnDirection = async (
    token: string,
    sequence_name: string,
    frame: string,
    ratioOg: number,
    diff: number,
  ): Promise<void> => {
    const inverse_ratioOg = 1 / ratioOg;

    // Full-fetch fallback used when the buffer is stale or empty.
    const fullFetch = async () => {
      nextFrameRef.current = "";
      prevFrameRef.current = "";
      await Promise.all([
        fetchAndSetImages(token, sequence_name, frame, setSequenceData).then(
          (result) => setSelections(adjustToScale(result, inverse_ratioOg)),
        ),
        fetchAndSetImages(
          token,
          sequence_name,
          frameMinusOne(frame),
          setSequenceDataPrev,
          (f) => {
            prevFrameRef.current = f;
          },
        ).then((result) =>
          setSelectionsPrev(adjustToScale(result, inverse_ratioOg)),
        ),
        fetchAndSetImages(
          token,
          sequence_name,
          framePlusOne(frame),
          setSequenceDataNext,
          (f) => {
            nextFrameRef.current = f;
          },
        ).then((result) =>
          setSelectionsNext(adjustToScale(result, inverse_ratioOg)),
        ),
      ]);
    };

    switch (diff) {
      case 1: {
        // The buffer is valid only if the previously prefetched frame matches
        // the frame we are navigating TO.
        const isCacheValid =
          nextFrameRef.current === frame && sequenceDataNext.images.length > 0;

        if (isCacheValid) {
          // Fast path: promote buffered next → current, current → prev.
          const seqNextCopy = JSON.parse(JSON.stringify(sequenceDataNext));
          const selNextCopy = JSON.parse(JSON.stringify(selectionsNext));
          const seqCurCopy = JSON.parse(JSON.stringify(sequenceData));
          const selCurCopy = JSON.parse(JSON.stringify(selections));

          setSequenceData(seqNextCopy);
          setSelections(selNextCopy);
          setSequenceDataPrev(seqCurCopy);
          setSelectionsPrev(selCurCopy);
          // The prev buffer now holds what was current (frameMinusOne of the target).
          prevFrameRef.current = frameMinusOne(frame);

          // Invalidate the next-buffer ref and prefetch the new next frame.
          // We await this so the caller knows navigation is safe again only
          // after the new prefetch has landed.
          nextFrameRef.current = "";
          await fetchAndSetImages(
            token,
            sequence_name,
            framePlusOne(frame),
            setSequenceDataNext,
            (f) => {
              nextFrameRef.current = f;
            },
          ).then((result) =>
            setSelectionsNext(adjustToScale(result, inverse_ratioOg)),
          );
        } else {
          // Slow path: buffer was stale – fetch everything fresh.
          await fullFetch();
        }
        break;
      }

      case -1: {
        const isCacheValid =
          prevFrameRef.current === frame && sequenceDataPrev.images.length > 0;

        if (isCacheValid) {
          const seqPrevCopy = JSON.parse(JSON.stringify(sequenceDataPrev));
          const selPrevCopy = JSON.parse(JSON.stringify(selectionsPrev));
          const seqCurCopy = JSON.parse(JSON.stringify(sequenceData));
          const selCurCopy = JSON.parse(JSON.stringify(selections));

          setSequenceData(seqPrevCopy);
          setSelections(selPrevCopy);
          setSequenceDataNext(seqCurCopy);
          setSelectionsNext(selCurCopy);
          // The next buffer now holds what was current (framePlusOne of the target).
          nextFrameRef.current = framePlusOne(frame);

          prevFrameRef.current = "";
          await fetchAndSetImages(
            token,
            sequence_name,
            frameMinusOne(frame),
            setSequenceDataPrev,
            (f) => {
              prevFrameRef.current = f;
            },
          ).then((result) =>
            setSelectionsPrev(adjustToScale(result, inverse_ratioOg)),
          );
        } else {
          await fullFetch();
        }
        break;
      }
    }
  };

  const setImagesAndSelections = async (
    token: string,
    sequence_name: string,
    frame: string,
    diffFs: number,
    prevFrame: string,
    ratioOg: number,
  ): Promise<void> => {
    let diff = 0;
    if (diffFs === 0 && frame.length > 0 && prevFrame.length > 0) {
      const new_frame_number = frame.split("image_")[1].split("-frame")[0];
      const prev_frame_number = prevFrame.includes("image_")
        ? prevFrame.split("image_")[1].split("-frame")[0]
        : "";
      let strNumber = parseInt(new_frame_number, 10);
      let strPrevNumber = parseInt(prev_frame_number, 10);
      diff = strNumber - strPrevNumber;
    } else {
      diff = diffFs;
    }

    const inverse_ratioOg = 1 / ratioOg;

    if (Math.abs(diff) !== 1) {
      // Jump or initial load: always fetch all three frames fresh.
      nextFrameRef.current = "";
      prevFrameRef.current = "";
      await Promise.all([
        fetchAndSetImages(token, sequence_name, frame, setSequenceData).then(
          (result) => setSelections(adjustToScale(result, inverse_ratioOg)),
        ),
        fetchAndSetImages(
          token,
          sequence_name,
          frameMinusOne(frame),
          setSequenceDataPrev,
          (f) => {
            prevFrameRef.current = f;
          },
        ).then((result) => {
          setSelectionsPrev(adjustToScale(result, inverse_ratioOg));
        }),
        fetchAndSetImages(
          token,
          sequence_name,
          framePlusOne(frame),
          setSequenceDataNext,
          (f) => {
            nextFrameRef.current = f;
          },
        ).then((result) => {
          setSelectionsNext(adjustToScale(result, inverse_ratioOg));
        }),
      ]);
    } else {
      // Sequential navigation: validate cache, await the full operation
      // (including prefetch) so the caller only unblocks once data is ready.
      await switchExistingStatesFetchNewImagesDependingOnDirection(
        token,
        sequence_name,
        frame,
        ratioOg,
        diff,
      );
    }

    clearSelectionHistory();
  };

  const clearSelections = useCallback(() => {
    clearSelectionHistory();
    setSelections([]);
    setSelectionsNext([]);
    setSelectionsPrev([]);
  }, [clearSelectionHistory]);

  const clearImages = useCallback(() => {
    setSequenceData({
      sequenceName: "",
      images: [],
    });
    setSequenceDataNext({
      sequenceName: "",
      images: [],
    });
    setSequenceDataPrev({
      sequenceName: "",
      images: [],
    });
  }, []);
  return (
    <Context.Provider
      value={{
        drawingMode,
        setDrawingMode,
        switchExistingStatesFetchNewImagesDependingOnDirection,
        selections,
        currentImageIndex,
        currentImageRect,
        fullImageRatioToOg,
        fullScreenWidth,
        sequenceData,
        prev,
        setSelections,
        addNewSelection,
        setCurrentImageIndex,
        setCurrentImageRect,
        setFullImageRatioToOg,
        setFullScreenWidth,
        copyPreviousToCurrent,
        fetchAndSetImages,
        setSequenceData,
        clearImages,
        clearSelections,
        setPrev,
        getUnmarkedMarked,
        setImagesAndSelections,
        currentTag,
        setCurrentTag,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default ContextProvider;
